/**
 * Persists last successful shelf/grid payloads so Zflix (web + WebView APK) can paint posters
 * immediately on reload while fetches run (stale-while-revalidate).
 * Uses localStorage; cleared on sign-out.
 */

const PREFIX = 'zflix_cache_';
const HOME_KEY = `${PREFIX}home_v2`;
const MOVIES_BROWSE_KEY = `${PREFIX}movies_browse_v2`;
const SHOWS_BROWSE_KEY = `${PREFIX}shows_browse_v2`;

function moviesGridKey(sort, searchQuery) {
  return `${PREFIX}movies_grid_v2:${String(sort || 'DateCreated')}:${encodeURIComponent(String(searchQuery || ''))}`;
}

function showsGridKey(searchQuery) {
  return `${PREFIX}shows_grid_v2:${encodeURIComponent(String(searchQuery || ''))}`;
}

function musicGridKey(searchQuery) {
  return `${PREFIX}music_grid_v2:${encodeURIComponent(String(searchQuery || ''))}`;
}

function safeJsonParse(raw, fallback) {
  try {
    const o = JSON.parse(raw);
    return o && typeof o === 'object' ? o : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const s = JSON.stringify(value);
    if (s.length > 4_200_000) return;
    window.localStorage.setItem(key, s);
  } catch (e) {
    if (e && (e.name === 'QuotaExceededError' || e.code === 22)) {
      try {
        clearZflixShelfCaches();
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (_) {}
    }
  }
}

function readJson(key) {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  const raw = window.localStorage.getItem(key);
  if (!raw) return null;
  return safeJsonParse(raw, null);
}

export function readHomeShelfCache() {
  const d = readJson(HOME_KEY);
  if (!d || d.v !== 2) return null;
  return {
    hero: d.hero || null,
    movies: Array.isArray(d.movies) ? d.movies : [],
    shows: Array.isArray(d.shows) ? d.shows : [],
    music: Array.isArray(d.music) ? d.music : [],
    audiobooks: Array.isArray(d.audiobooks) ? d.audiobooks : [],
    favoriteTv: Array.isArray(d.favoriteTv) ? d.favoriteTv : [],
    resume: Array.isArray(d.resume) ? d.resume : [],
  };
}

export function writeHomeShelfCache(payload) {
  writeJson(HOME_KEY, {
    v: 2,
    savedAt: Date.now(),
    hero: payload.hero || null,
    movies: payload.movies || [],
    shows: payload.shows || [],
    music: payload.music || [],
    audiobooks: payload.audiobooks || [],
    favoriteTv: payload.favoriteTv || [],
    resume: payload.resume || [],
  });
}

export function readMoviesGridCache(sort, searchQuery) {
  const d = readJson(moviesGridKey(sort, searchQuery));
  if (!d || d.v !== 2) return null;
  return {
    items: Array.isArray(d.items) ? d.items : [],
    total: typeof d.total === 'number' ? d.total : 0,
  };
}

export function writeMoviesGridCache(sort, searchQuery, { items, total }) {
  writeJson(moviesGridKey(sort, searchQuery), {
    v: 2,
    savedAt: Date.now(),
    items: Array.isArray(items) ? items : [],
    total: typeof total === 'number' ? total : (Array.isArray(items) ? items.length : 0),
  });
}

export function readMoviesBrowseCache() {
  const d = readJson(MOVIES_BROWSE_KEY);
  if (!d || d.v !== 2) return null;
  return {
    resumeM: Array.isArray(d.resumeM) ? d.resumeM : [],
    recentM: Array.isArray(d.recentM) ? d.recentM : [],
    genreRows: Array.isArray(d.genreRows) ? d.genreRows : [],
    collectionRows: Array.isArray(d.collectionRows) ? d.collectionRows : [],
  };
}

export function writeMoviesBrowseCache({ resumeM, recentM, genreRows, collectionRows }) {
  writeJson(MOVIES_BROWSE_KEY, {
    v: 2,
    savedAt: Date.now(),
    resumeM: resumeM || [],
    recentM: recentM || [],
    genreRows: genreRows || [],
    collectionRows: collectionRows || [],
  });
}

export function readShowsGridCache(searchQuery) {
  const d = readJson(showsGridKey(searchQuery));
  if (!d || d.v !== 2) return null;
  return {
    items: Array.isArray(d.items) ? d.items : [],
    total: typeof d.total === 'number' ? d.total : 0,
  };
}

export function writeShowsGridCache(searchQuery, { items, total }) {
  writeJson(showsGridKey(searchQuery), {
    v: 2,
    savedAt: Date.now(),
    items: Array.isArray(items) ? items : [],
    total: typeof total === 'number' ? total : (Array.isArray(items) ? items.length : 0),
  });
}

export function readShowsBrowseCache() {
  const d = readJson(SHOWS_BROWSE_KEY);
  if (!d || d.v !== 2) return null;
  return {
    resumeE: Array.isArray(d.resumeE) ? d.resumeE : [],
    recentS: Array.isArray(d.recentS) ? d.recentS : [],
    genreRows: Array.isArray(d.genreRows) ? d.genreRows : [],
    collectionRows: Array.isArray(d.collectionRows) ? d.collectionRows : [],
  };
}

export function writeShowsBrowseCache({ resumeE, recentS, genreRows, collectionRows }) {
  writeJson(SHOWS_BROWSE_KEY, {
    v: 2,
    savedAt: Date.now(),
    resumeE: resumeE || [],
    recentS: recentS || [],
    genreRows: genreRows || [],
    collectionRows: collectionRows || [],
  });
}

export function readMusicGridCache(searchQuery) {
  const d = readJson(musicGridKey(searchQuery));
  if (!d || d.v !== 2) return null;
  return {
    items: Array.isArray(d.items) ? d.items : [],
    total: typeof d.total === 'number' ? d.total : 0,
  };
}

export function writeMusicGridCache(searchQuery, { items, total }) {
  writeJson(musicGridKey(searchQuery), {
    v: 2,
    savedAt: Date.now(),
    items: Array.isArray(items) ? items : [],
    total: typeof total === 'number' ? total : (Array.isArray(items) ? items.length : 0),
  });
}

/** Remove all Zflix shelf cache entries (call on sign-out). */
export function clearZflixShelfCaches() {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const keys = Object.keys(window.localStorage);
    for (const k of keys) {
      if (k.startsWith(PREFIX)) window.localStorage.removeItem(k);
    }
  } catch (_) {}
}

export function homeShelfHasContent(c) {
  if (!c) return false;
  return !!(
    c.hero ||
    (c.movies && c.movies.length) ||
    (c.shows && c.shows.length) ||
    (c.music && c.music.length) ||
    (c.audiobooks && c.audiobooks.length) ||
    (c.favoriteTv && c.favoriteTv.length) ||
    (c.resume && c.resume.length)
  );
}

/** Drop cached Jellyfin resume rows so UI refetch does not flash stale positions. */
export function invalidateMoviesBrowseResume() {
  const d = readMoviesBrowseCache();
  if (!d) return;
  writeMoviesBrowseCache({ ...d, resumeM: [] });
}

export function invalidateShowsBrowseResume() {
  const d = readShowsBrowseCache();
  if (!d) return;
  writeShowsBrowseCache({ ...d, resumeE: [] });
}

export function invalidateHomeResume() {
  const d = readJson(HOME_KEY);
  if (!d || d.v !== 2) return;
  writeJson(HOME_KEY, { ...d, resume: [] });
}
